// Turning a farm's stored Sentinel-2 PNGs into the picture laid over the map.
// The server keeps the raw bands (types/satellite.ts SatelliteImages); colour,
// stretch and clipping to the farm, block or cultivar area happen here, so one
// stored image serves every place on the farm. The figures shown beside it are
// the server's (HHNodeServer utils/satelliteRaster.statsFor), never these.

import type { GeoJSONPolygon } from '../types/orchard';
import type { SatelliteBBox, SatelliteLayer, SatelliteScale } from '../types/satellite';

/** Scene classes that are not ground: no data, defective, cloud shadow, cloud, cirrus, snow. Mirrors the server. */
export const UNREADABLE_SCL = new Set([0, 1, 3, 8, 9, 10, 11]);

/** The fixed colour scale for each index: an orchard canopy sits towards the top of both. */
export const FIXED_RANGE: Record<'ndvi' | 'ndre', [number, number]> = {
  ndvi: [0.1, 0.9],
  ndre: [0.05, 0.6],
};

/** Low to high: red, orange, yellow, light green, green. */
export const RAMP: [number, number, number][] = [
  [215, 48, 39],
  [252, 141, 89],
  [254, 224, 139],
  [145, 207, 96],
  [26, 152, 80],
];

export const RAMP_CSS = `linear-gradient(to right, ${RAMP.map(([r, g, b]) => `rgb(${r},${g},${b})`).join(', ')})`;

export const LAYER_LABEL: Record<SatelliteLayer, string> = {
  ndvi: 'NDVI',
  ndre: 'NDRE',
  nir: 'Near-infrared',
};

export const LAYER_HELP: Record<SatelliteLayer, string> = {
  ndvi: 'How much green canopy each 10 m square holds. Low patches are thin, stressed or missing trees, or bare ground.',
  ndre: 'Like NDVI, but still separates dense canopy where NDVI levels off. Falls first with nitrogen or water stress.',
  nir: 'False colour: near-infrared shown as red, so healthy canopy is bright red and soil, roads and dams are grey or dark.',
};

/** An index byte back to its value: the server stores (v + 1) × 125. */
export const decodeIndex = (byte: number): number => byte / 125 - 1;

/** A colour on the ramp for `t` from 0 (low) to 1 (high). */
export function rampColour(t: number): [number, number, number] {
  const x = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0)) * (RAMP.length - 1);
  const i = Math.min(RAMP.length - 2, Math.floor(x));
  const f = x - i;
  const [a, b] = [RAMP[i], RAMP[i + 1]];
  return [0, 1, 2].map((k) => Math.round(a[k] + (b[k] - a[k]) * f)) as [number, number, number];
}

/**
 * The colour scale stretched to the values present: the 2nd to 98th
 * percentile, never narrower than 0.05 so a uniform block is not painted as
 * though it were all extremes.
 */
export function stretchRange(values: number[], minSpan = 0.05): [number, number] | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * q)))];
  let lo = at(0.02);
  let hi = at(0.98);
  if (hi - lo < minSpan) {
    const mid = (lo + hi) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }
  return [lo, hi];
}

function inRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring and none of the holes. Mirrors the server, so a pixel counted there is drawn here. */
export function polygonContains(polygon: GeoJSONPolygon, lng: number, lat: number): boolean {
  const [outer, ...holes] = polygon.coordinates ?? [];
  if (!outer || !inRing(lng, lat, outer)) return false;
  return !holes.some((h) => inRing(lng, lat, h));
}

/**
 * Which pixels of a width × height grid over `bbox` have their centre inside
 * the polygon: one byte per pixel.
 */
export function insideMask(polygon: GeoJSONPolygon, bbox: SatelliteBBox, width: number, height: number): Uint8Array {
  const mask = new Uint8Array(width * height);
  const [w, s, e, n] = bbox;
  const dx = (e - w) / width;
  const dy = (n - s) / height;
  const ring = polygon.coordinates?.[0] ?? [];
  if (!ring.length) return mask;
  const lngs = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  const x0 = Math.max(0, Math.floor((Math.min(...lngs) - w) / dx));
  const x1 = Math.min(width - 1, Math.ceil((Math.max(...lngs) - w) / dx));
  const y0 = Math.max(0, Math.floor((n - Math.max(...lats)) / dy));
  const y1 = Math.min(height - 1, Math.ceil((n - Math.min(...lats)) / dy));
  for (let y = y0; y <= y1; y++) {
    const lat = n - (y + 0.5) * dy;
    for (let x = x0; x <= x1; x++) {
      if (polygonContains(polygon, w + (x + 0.5) * dx, lat)) mask[y * width + x] = 1;
    }
  }
  return mask;
}

export interface PaintOptions {
  layer: SatelliteLayer;
  scale: SatelliteScale;
  /** 1 where the pixel is inside the place, from insideMask. */
  mask: Uint8Array;
}

/** The colour range a paint used, for the legend. Null for near-infrared or nothing clear. */
export type PaintRange = [number, number] | null;

const CLOUD: [number, number, number, number] = [255, 255, 255, 150];

/**
 * Colour an index image in place. `index` is the decoded index PNG; `nir`, when
 * painting near-infrared, is the false-colour PNG. Outside the place is
 * transparent; cloud and shadow are a pale veil so a gap reads as cloud, not
 * as bare ground. Returns the range the colours run over.
 */
export function paint(out: Uint8ClampedArray, index: Uint8ClampedArray, nir: Uint8ClampedArray | null, opts: PaintOptions): PaintRange {
  const n = opts.mask.length;
  const band = opts.layer === 'ndre' ? 1 : 0;
  const clear = (i: number) => opts.mask[i] === 1 && index[i * 4 + 3] !== 0 && !UNREADABLE_SCL.has(index[i * 4 + 2]);

  let range: PaintRange = null;
  if (opts.layer !== 'nir') {
    if (opts.scale === 'stretch') {
      const values: number[] = [];
      for (let i = 0; i < n; i++) if (clear(i)) values.push(decodeIndex(index[i * 4 + band]));
      range = stretchRange(values);
    }
    range = range ?? FIXED_RANGE[opts.layer];
  }

  for (let i = 0; i < n; i++) {
    const o = i * 4;
    if (opts.mask[i] !== 1 || index[o + 3] === 0) {
      out[o + 3] = 0;
      continue;
    }
    if (opts.layer === 'nir' && nir) {
      out[o] = nir[o]; out[o + 1] = nir[o + 1]; out[o + 2] = nir[o + 2]; out[o + 3] = 255;
      continue;
    }
    if (!clear(i)) {
      [out[o], out[o + 1], out[o + 2], out[o + 3]] = CLOUD;
      continue;
    }
    const [lo, hi] = range as [number, number];
    const [r, g, b] = rampColour((decodeIndex(index[o + band]) - lo) / (hi - lo));
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = 255;
  }
  return opts.layer === 'nir' ? null : range;
}

/** A data URL's pixels, through a canvas. */
export async function pixelsOf(src: string): Promise<ImageData> {
  const img = new Image();
  img.src = src;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.drawImage(img, 0, 0);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/** Painted pixels to a PNG data URL for an image overlay. */
export function toDataUrl(width: number, height: number, pixels: Uint8ClampedArray): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const data = ctx.createImageData(width, height);
  data.data.set(pixels);
  ctx.putImageData(data, 0, 0);
  return canvas.toDataURL('image/png');
}

/** "0.62" — two places, as the figures are read. */
export const fmtIndex = (v: number | null | undefined): string => (v == null ? '—' : v.toFixed(2));
